import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const BRAND = '#8fbd56';

export interface BarChartSeries {
  label?: string;
  data: (number | null)[];
  color?: string;
}

interface BarChartProps {
  labels: string[];
  series: BarChartSeries[];
  height?: number | string;
  options?: ChartOptions<'bar'>;
  // When there's no data, the chart still draws its axes/grid as a placeholder
  // frame (so it's clear a chart belongs here), with this hint centered over it.
  emptyHint?: ReactNode;
}

// Thin reusable Chart.js bar chart (matches the legacy Chart component defaults:
// responsive, legend hidden for single-series, brand green).
export function BarChart({ labels, series, height = 260, options, emptyHint }: BarChartProps) {
  const isEmpty = !series.some((s) => s.data.some((v) => v != null));
  const data = useMemo<ChartData<'bar'>>(
    () => ({
      labels,
      datasets: series.map((s) => ({
        label: s.label ?? '',
        data: s.data,
        backgroundColor: s.color ?? BRAND,
        borderRadius: 3,
        maxBarThickness: 42,
      })),
    }),
    [labels, series],
  );

  const mergedOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: series.length > 1 && !isEmpty }, tooltip: { enabled: !isEmpty } },
      // Empty => draw a clean grid frame (lines only, no tick numbers) as a placeholder.
      scales: isEmpty
        ? {
            x: { grid: { display: true, color: 'rgba(0,0,0,0.05)' }, ticks: { display: false } },
            y: { suggestedMin: 0, suggestedMax: 100, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { display: false } },
          }
        : { x: { grid: { display: false } }, y: { grid: { color: 'rgba(0,0,0,0.05)' } } },
      ...options,
    }),
    [options, series.length, isEmpty],
  );

  return (
    <div style={{ height, position: 'relative' }}>
      <Bar data={data} options={mergedOptions} />
      {isEmpty && emptyHint != null && (
        <div
          className="text-muted small"
          style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
        >
          {emptyHint}
        </div>
      )}
    </div>
  );
}
